import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Text,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Input,
  Flex,
  SkeletonText,
} from '@chakra-ui/react';
import { AttachmentIcon, CloseIcon } from '@chakra-ui/icons';
import { MdDelete, MdFileDownload } from 'react-icons/md';
import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  handleDownloadFile,
  handleFileInputChange as handleFileInput,
  removeAttachedFile as removeFile,
  extractFileName,
} from 'utils/fileHelper';

import reactQuillOptions from 'utils/constants/reactQuillOptions';

import { postFileSubmissionSessionAction } from 'store/modules/progression/progression-actions';
import { progressionActions } from 'store/modules/progression/progression-slice';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { getLatestFileSubmissionSession } from 'store/modules/subquest/subquest-action';
import { ReducerStatus } from 'utils/constants';
import ConfirmationActionModal from 'components/common/modals/ConfirmationActionModal';

import parse from 'html-react-parser';
import DOMPurify from 'dompurify';

import './styles/QuillContentStyling.css';
import useSubQuestAutoPlay from 'hooks/useSubQuestAutoPlay';
import {
  ONE_TIME_SUBMISSION,
  OVERWRITE_EXISTING_SESSION,
} from 'utils/constants/submissionType';
import { resultTextColors } from 'utils/constants/result';

function FileSubmissionSubQuest({
  classId,
  course,
  quest,
  subQuest,
  code,
  quests,
}) {
  const dispatch = useDispatch();

  const postFileSubmissionStatus = useSelector(
    (state) => state.progression.postFileSubmissionStatus
  );

  const fileSubmissionSession = useSelector(
    (state) => state.subquest.fileSubmissionSession
  );

  const getLatestSession = useSelector(
    (state) => state.subquest.getLatestFileSubmissionSession
  );

  const isOneTimeSubmission =
    subQuest.subQuestFileSubmission?.answerSetting === ONE_TIME_SUBMISSION;

  const isOverwriteSession =
    subQuest.subQuestFileSubmission?.answerSetting ===
    OVERWRITE_EXISTING_SESSION;

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [filesToRemove, setFilesToRemove] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasSubmittedFromSession, setHasSubmittedFromSession] = useState(false);
  const [originalSessionFiles, setOriginalSessionFiles] = useState([]);

  const fileInputRef = useRef(null);

  const { triggerAutoPlay } = useSubQuestAutoPlay({
    courseId: course.id,
    questId: quest.id,
    subQuestId: subQuest.id,
    classId,
    quests,
  });

  const baseFileUrl = process.env.REACT_APP_FILE_URL;

  const sanitizedContent = DOMPurify.sanitize(
    subQuest.subQuestFileSubmission.requirements,
    reactQuillOptions
  );

  const bgColor = useColorModeValue('gray.50', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');

  const infoBlueBg = useColorModeValue('blue.50', 'blue.700');
  const infoRedBg = useColorModeValue('red.50', 'red.700');
  const infoTextColor = useColorModeValue('black', 'white');

  useEffect(() => {
    dispatch(
      getLatestFileSubmissionSession({
        courseId: course.id,
        questId: quest.id,
        subQuestId: subQuest.id,
      })
    );
  }, [dispatch, course.id, quest.id, subQuest.id]);

  useEffect(() => {
    if (getLatestSession.status === ReducerStatus.LOADING) {
      setAttachedFiles([]);
      setFilesToRemove([]);
      setOriginalSessionFiles([]);
      setShowConfirmation(false);
      setError(null);
      setHasSubmitted(false);
      setHasSubmittedFromSession(false);
    }
  }, [getLatestSession.status]);

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event) => {
    handleFileInput(event, setAttachedFiles);
  };

  const removeAttachedFile = (fileId) => {
    const fileToRemove = attachedFiles.find((file) => file.id === fileId);

    if (fileToRemove && fileToRemove.isFromSession) {
      setFilesToRemove((prev) => [...prev, fileToRemove.filePath]);
    }

    removeFile(fileId, setAttachedFiles);
  };

  const handleSubmitFiles = () => {
    dispatch(progressionActions.resetFileSubmissionStatus());
    setShowConfirmation(true);
  };

  const handleConfirmSubmission = async () => {
    try {
      const newFiles = attachedFiles.filter((file) => file.file !== null);

      if (newFiles.length === 0 && filesToRemove.length === 0) {
        throw new Error('No changes to submit');
      }

      const zipFiles = newFiles.map((fileItem) => {
        const nameWithoutZip = fileItem.file.name.replace(/\.zip$/i, '');
        return new File([fileItem.file], nameWithoutZip, {
          type: 'application/x-zip-compressed',
          lastModified: fileItem.file.lastModified,
        });
      });

      const response = await dispatch(
        postFileSubmissionSessionAction({
          classId,
          courseId: course.id,
          questId: quest.id,
          subQuestId: subQuest.id,
          code,
          submittedFiles: zipFiles,
          filesToRemove,
        })
      ).unwrap();

      setFilesToRemove([]);
      setAttachedFiles([]);
      setOriginalSessionFiles([]);
      setHasSubmitted(true);

      dispatch(
        subquestActions.updateFileSubmissionSession({
          filePaths: response.filePaths,
          status: response.status,
        })
      );

      dispatch(progressionActions.updateProgression(response));

      if (response && response.dateFinished) {
        triggerAutoPlay();
      }
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => {
    if (
      fileSubmissionSession &&
      fileSubmissionSession.filePaths &&
      fileSubmissionSession.filePaths.length > 0
    ) {
      const sessionFiles = fileSubmissionSession.filePaths.map((filePath) => ({
        id: uuidv4(),
        name: extractFileName(filePath, `Project_Template.zip`),
        file: null,
        isFromSession: true,
        filePath,
      }));

      setAttachedFiles(sessionFiles);
      setOriginalSessionFiles(sessionFiles);
      setHasSubmittedFromSession(true);
    }
  }, [fileSubmissionSession]);

  const handleCancelConfirmation = () => {
    setAttachedFiles(originalSessionFiles);
    setFilesToRemove([]);
    setShowConfirmation(false);
  };

  const handleConfirmationResult = () => {
    setShowConfirmation(false);
  };

  const getConfirmationDescription = () => {
    const newFiles = attachedFiles.filter((file) => file.file !== null);

    if (newFiles.length === 0 && filesToRemove.length === 0) return '';

    return (
      <>
        <Text mb={3}>Are you sure you want to submit these changes?</Text>
        <VStack align="start" spacing={1}>
          {newFiles.length > 0 && (
            <>
              <Text fontWeight="bold">Files to add:</Text>
              {newFiles.map((fileItem) => (
                <Text key={fileItem.id} fontSize="sm" color="green.600">
                  • {fileItem.name}
                </Text>
              ))}
            </>
          )}
          {filesToRemove.length > 0 && (
            <>
              <Text fontWeight="bold" mt={2}>
                Files to remove:
              </Text>
              {filesToRemove.map((fileName) => {
                const extractedFileName = extractFileName(
                  fileName,
                  `Uploaded_Project.zip`
                );
                return (
                  <Text key={uuidv4()} fontSize="sm" color="red.600">
                    • {extractedFileName}
                  </Text>
                );
              })}
            </>
          )}
        </VStack>
      </>
    );
  };

  const renderAttachedFiles = () => {
    const filePaths = subQuest?.subQuestFileSubmission?.filePaths;

    if (!filePaths || filePaths.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" fontStyle="italic">
          No template files available
        </Text>
      );
    }

    return filePaths.map((filePath) => {
      const fileName = extractFileName(filePath, `Project_Template.zip`);
      return (
        <Box
          key={filePath}
          p={3}
          bg={bgColor}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="md"
          _hover={{ bg: hoverBg, cursor: 'pointer' }}
          onClick={() => handleDownloadFile(baseFileUrl, filePath, fileName)}
          transition="background-color 0.2s"
        >
          <HStack spacing={3}>
            <Box fontSize="lg">📁</Box>
            <VStack align="start" spacing={0} flex={1}>
              <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                {fileName}
              </Text>
              <Text fontSize="xs" color="gray.500">
                ZIP Archive
              </Text>
            </VStack>
            <Icon as={MdFileDownload} color="blue.500" boxSize={5} />
          </HStack>
        </Box>
      );
    });
  };

  const renderUserAttachedFiles = () => {
    if (attachedFiles.length === 0) {
      return (
        <Text fontSize="sm" color="gray.500" fontStyle="italic">
          No files attached yet
        </Text>
      );
    }

    const sessionFiles = attachedFiles.filter(
      (fileItem) => fileItem.isFromSession
    );
    const uploadedFiles = attachedFiles.filter(
      (fileItem) => !fileItem.isFromSession
    );

    return (
      <>
        {/* Files from session */}
        {sessionFiles.length > 0 && (
          <>
            <Text fontSize="sm" fontWeight="semibold">
              Uploaded Files:
            </Text>
            {sessionFiles.map((fileItem) => (
              <Box
                key={fileItem.id}
                p={3}
                bg={bgColor}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                transition="background-color 0.2s"
              >
                <HStack spacing={3}>
                  <Box fontSize="lg">📁</Box>
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                      {fileItem.name}
                    </Text>
                  </VStack>
                  <Flex align="center" gap={2}>
                    <Icon
                      as={MdFileDownload}
                      color="blue.500"
                      boxSize={5}
                      cursor="pointer"
                      onClick={() =>
                        handleDownloadFile(
                          baseFileUrl,
                          fileItem.filePath,
                          fileItem.name
                        )
                      }
                    />
                    {!isOneTimeSubmission && !isOverwriteSession && (
                      <Icon
                        as={MdDelete}
                        color="red.500"
                        boxSize={5}
                        cursor="pointer"
                        onClick={() => removeAttachedFile(fileItem.id)}
                      />
                    )}
                  </Flex>
                </HStack>
              </Box>
            ))}
          </>
        )}

        {uploadedFiles.length === 0 &&
          subQuest.subQuestFileSubmission?.answerSetting ===
            OVERWRITE_EXISTING_SESSION && (
            <Box
              p={3}
              bg={infoRedBg}
              borderRadius="md"
              border="1px solid"
              borderColor="red.200"
            >
              <Text fontSize="xs" color={infoTextColor}>
                <strong>Info:</strong> A new ZIP file is required to be attached
                before submission.
              </Text>
            </Box>
          )}
        {/* Files to upload */}
        {uploadedFiles.length > 0 && (
          <>
            <Text fontSize="sm" fontWeight="semibold" mt={2}>
              Files to upload:
            </Text>
            {uploadedFiles.map((fileItem) => (
              <Box
                key={fileItem.id}
                p={3}
                bg={bgColor}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                transition="background-color 0.2s"
              >
                <HStack spacing={3}>
                  <Box fontSize="lg">📁</Box>
                  <VStack align="start" spacing={0} flex={1}>
                    <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
                      {fileItem.name}
                    </Text>
                  </VStack>
                  <Flex align="center" gap={2}>
                    <Icon
                      as={MdFileDownload}
                      color="blue.500"
                      boxSize={5}
                      cursor="pointer"
                      onClick={() =>
                        handleDownloadFile(
                          baseFileUrl,
                          fileItem.filePath,
                          fileItem.name
                        )
                      }
                    />
                    <CloseIcon
                      boxSize={4}
                      color="red.500"
                      cursor="pointer"
                      onClick={() => removeAttachedFile(fileItem.id)}
                    />
                  </Flex>
                </HStack>
              </Box>
            ))}
          </>
        )}
      </>
    );
  };

  const isSubmissionDisabled = () => {
    const hasNoChanges =
      attachedFiles.filter((file) => file.file !== null).length === 0 &&
      filesToRemove.length === 0;
    const hasExistingSubmission =
      fileSubmissionSession?.filePaths?.length > 0 || hasSubmitted;

    return hasNoChanges || (isOneTimeSubmission && hasExistingSubmission);
  };

  const getSubmissionButtonText = () => {
    const hasExistingSubmission = fileSubmissionSession?.filePaths?.length > 0;
    if (
      (hasSubmitted && isOneTimeSubmission) ||
      (hasExistingSubmission && isOneTimeSubmission)
    ) {
      return 'Already Submitted';
    }
    return 'Turn In';
  };

  const getResultTextColor = (result) => {
    return resultTextColors[result] || resultTextColors.Default;
  };

  return (
    <>
      <ConfirmationActionModal
        isVisible={showConfirmation}
        onClose={handleCancelConfirmation}
        onConfirm={handleConfirmSubmission}
        onSubmitResult={handleConfirmationResult}
        status={postFileSubmissionStatus}
        successMessage="Your file has been submitted successfully for review."
        error={error}
      >
        {getConfirmationDescription()}
      </ConfirmationActionModal>

      {getLatestSession.status === ReducerStatus.LOADING ? (
        <SkeletonText mb={4} noOfLines={3} />
      ) : (
        <Box className="submission-container" p={4}>
          <Box className="file-submission-content">
            <VStack spacing={4} align="stretch">
              <Text fontSize="3xl" fontWeight="bold">
                {subQuest.name}
              </Text>
              <Box className="submission-content">
                {parse(sanitizedContent)}
              </Box>

              {/* Submission Settings Section */}
              <Flex w="full">
                <Box flex="1" p={2} justifyItems="center">
                  <Text fontWeight="bold">Submission Settings</Text>
                  <Text>{subQuest.subQuestFileSubmission?.answerSetting}</Text>
                </Box>
                <Box flex="1" p={2} justifyItems="center">
                  <Text fontWeight="bold">Points</Text>
                  <Text>
                    {subQuest.subQuestFileSubmission?.totalScore} points
                    possible
                  </Text>
                </Box>
                <Box flex="1" p={2} justifyItems="center">
                  <Text fontWeight="bold">Status</Text>
                  <Text>
                    {fileSubmissionSession?.status || 'Not Submitted Yet'}
                  </Text>
                </Box>
              </Flex>

              {/* Submission Details Section */}
              {fileSubmissionSession &&
                fileSubmissionSession.status === 'Graded' && (
                  <Flex>
                    <Box flex="1" p={2} justifyItems="center">
                      <Text fontWeight="bold">Score</Text>
                      <Text>{fileSubmissionSession?.score}</Text>
                    </Box>
                    <Box flex="1" p={2} justifyItems="center">
                      <Text fontWeight="bold">Result</Text>
                      <Text
                        color={getResultTextColor(
                          fileSubmissionSession?.result
                        )}
                      >
                        {fileSubmissionSession?.result}
                      </Text>
                    </Box>
                    {fileSubmissionSession?.showRemarks && (
                      <Box flex="1" p={2} justifyItems="center">
                        <Text fontWeight="bold">Remarks</Text>
                        <Text>{fileSubmissionSession?.remarks}</Text>
                      </Box>
                    )}
                  </Flex>
                )}

              {/* Reference Materials Section */}
              <Box>
                <Text fontSize="md" fontWeight="semibold" mb={3}>
                  Reference Materials
                </Text>
                <VStack spacing={2} align="stretch">
                  {renderAttachedFiles()}
                </VStack>
              </Box>
              {/* Additional Info */}
              <Box
                p={3}
                bg={infoBlueBg}
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200"
              >
                <Text fontSize="xs" color={infoTextColor}>
                  <strong>Info:</strong> Only ZIP files are accepted. You can
                  attach multiple files.
                </Text>
              </Box>

              {/* Attached Files Section */}
              <Box mt={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <Text fontSize="md" fontWeight="semibold">
                    My Work
                  </Text>
                </Flex>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".zip"
                  multiple
                  hidden
                />
                <VStack spacing={2} align="stretch">
                  {renderUserAttachedFiles()}
                </VStack>
                <Button
                  size="sm"
                  colorScheme="red"
                  leftIcon={<AttachmentIcon />}
                  onClick={handleAttachFile}
                  isDisabled={hasSubmittedFromSession && isOneTimeSubmission}
                  variant="ghost"
                  mt={2}
                  p={0}
                >
                  Attach
                </Button>
                <Flex justifyContent="flex-end">
                  <Button
                    colorScheme="red"
                    size="md"
                    width="160px"
                    isDisabled={isSubmissionDisabled()}
                    isLoading={
                      postFileSubmissionStatus === ReducerStatus.LOADING
                    }
                    loadingText="Submitting..."
                    alignSelf="end"
                    mb={4}
                    onClick={handleSubmitFiles}
                  >
                    {getSubmissionButtonText()}
                  </Button>
                </Flex>
              </Box>
            </VStack>
          </Box>
        </Box>
      )}
    </>
  );
}

FileSubmissionSubQuest.defaultProps = {
  classId: null,
  quests: [],
};

FileSubmissionSubQuest.propTypes = {
  classId: PropTypes.number,
  subQuest: PropTypes.object.isRequired,
  course: PropTypes.object.isRequired,
  quest: PropTypes.object.isRequired,
  code: PropTypes.string.isRequired,
  quests: PropTypes.array,
};

export default FileSubmissionSubQuest;
